import type {
  AutomationSolution,
  CalculationResult,
  Client,
  ClientProfile,
  ManagedClient,
  Model,
  Scenario,
  SpendSet,
  StaffPlan,
} from '@rc/engine/types';
import { geoclientQuery } from './geoclientContext';

const BASE = '/api';
const CACHE_TTL_MS = 30_000;

type CacheEntry<T> = {
  expiresAt: number;
  promise: Promise<T>;
};

const responseCache = new Map<string, CacheEntry<unknown>>();

function cached<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const hit = responseCache.get(key) as CacheEntry<T> | undefined;
  if (hit && hit.expiresAt > now) return hit.promise;

  let promise: Promise<T>;
  promise = loader().catch((err) => {
    const current = responseCache.get(key);
    if (current?.promise === promise) responseCache.delete(key);
    throw err;
  });
  responseCache.set(key, { expiresAt: now + CACHE_TTL_MS, promise });
  return promise;
}

function clearCache(prefix?: string) {
  if (!prefix) {
    responseCache.clear();
    return;
  }
  for (const key of Array.from(responseCache.keys())) {
    if (key.startsWith(prefix)) responseCache.delete(key);
  }
}

async function mutate<T>(loader: () => Promise<T>, clearPrefixes: string[] = []): Promise<T> {
  const value = await loader();
  for (const prefix of clearPrefixes) clearCache(prefix);
  return value;
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(BASE + url, {
    ...init,
    credentials: 'same-origin',
    headers,
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    let failedRows: Array<{ row: number; message: string }> | undefined;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      // Fastify puts the real Postgres/S3 detail in `message`; surface it when the
      // status label alone is not actionable (e.g. "Internal Server Error").
      if (
        body?.message &&
        typeof body.message === 'string' &&
        body.message !== msg &&
        (msg === 'Internal Server Error' || msg.endsWith('Internal Server Error'))
      ) {
        msg = body.message;
      }
      if (Array.isArray(body?.failedRows)) failedRows = body.failedRows;
    } catch {
      // ignore
    }
    const err = new Error(msg) as Error & {
      failedRows?: Array<{ row: number; message: string }>;
    };
    if (failedRows) err.failedRows = failedRows;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // Auth
  me: () => request<AuthMe>('/auth/me'),
  authConfig: () => request<AuthConfig>('/auth/config'),
  logout: async () => {
    const value = await request<{ ok: true }>('/auth/logout', { method: 'POST' });
    clearCache();
    return value;
  },

  // Models
  listModels: () => request<Model[]>('/models'),
  listModelSummaries: () =>
    cached('models:summaries', () => request<ModelSummary[]>('/models?metadata=1')),
  getModel: (id: string) => request<Model>(`/models/${id}`),
  createModel: (m: Model) =>
    mutate(
      () => request<Model>('/models', { method: 'POST', body: JSON.stringify(m) }),
      ['models:', 'portfolio'],
    ),
  saveModel: (m: Model) =>
    mutate(
      () => request<Model>(`/models/${m.id}`, { method: 'PUT', body: JSON.stringify(m) }),
      ['models:', 'portfolio'],
    ),
  deleteModel: (id: string) =>
    mutate(() => request<{ ok: true }>(`/models/${id}`, { method: 'DELETE' }), [
      'models:',
      'portfolio',
    ]),
  forkModel: (id: string, body: { name?: string; newId?: string }) =>
    mutate(
      () => request<Model>(`/models/${id}/fork`, { method: 'POST', body: JSON.stringify(body) }),
      ['models:', 'portfolio'],
    ),

  // Clients
  listClients: () => cached('clients:list', () => request<Client[]>('/clients')),
  clientContentSummary: () =>
    cached('clients:content-summary', () => request<ClientContentSummary[]>('/clients/content-summary')),
  getClient: (id: string) => request<Client>(`/clients/${id}`),
  createClient: (c: Client) =>
    mutate(
      () => request<Client>('/clients', { method: 'POST', body: JSON.stringify(c) }),
      ['clients:', 'portfolio'],
    ),
  saveClient: (c: Client) =>
    mutate(
      () => request<Client>(`/clients/${c.id}`, { method: 'PUT', body: JSON.stringify(c) }),
      ['clients:', 'portfolio'],
    ),
  deleteClient: (id: string) =>
    mutate(() => request<{ ok: true }>(`/clients/${id}`, { method: 'DELETE' }), [
      'clients:',
      'portfolio',
    ]),

  // Managed clients (admin catalog)
  listManagedClients: () =>
    cached('managed-clients:list', () => request<ManagedClient[]>('/managed-clients')),
  getManagedClient: (id: string) => request<ManagedClient>(`/managed-clients/${id}`),
  createManagedClient: (m: ManagedClient) =>
    mutate(
      () =>
        request<ManagedClient>('/managed-clients', {
          method: 'POST',
          body: JSON.stringify(m),
        }),
      ['managed-clients:'],
    ),
  saveManagedClient: (m: ManagedClient) =>
    mutate(
      () =>
        request<ManagedClient>(`/managed-clients/${m.id}`, {
          method: 'PUT',
          body: JSON.stringify(m),
        }),
      ['managed-clients:'],
    ),
  deleteManagedClient: (id: string) =>
    mutate(() => request<{ ok: true }>(`/managed-clients/${id}`, { method: 'DELETE' }), [
      'managed-clients:',
    ]),

  // Geoclients (Platform API eligibility) — list is uncached so typeahead stays fresh
  listGeoclients: (params?: { marketCode?: string; clientCode?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params?.marketCode) qs.set('marketCode', params.marketCode);
    if (params?.clientCode) qs.set('clientCode', params.clientCode);
    if (params?.q) qs.set('q', params.q);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<EligibleGeoclient[]>(`/geoclients${suffix}`);
  },
  getGeoclient: (id: number | string) => request<EligibleGeoclient>(`/geoclients/${id}`),
  recordGeoclientAccess: (geoclientId: number | string) =>
    request<{ ok: true }>(`/geoclients/${geoclientId}/access`, { method: 'POST' }),
  listGeoclientFilterMarkets: () =>
    cached('geoclients:markets', () =>
      request<GeoclientFilterMarket[]>('/geoclients/filter-options/markets'),
    ),
  listGeoclientFilterClients: () =>
    cached('geoclients:clients', () =>
      request<GeoclientFilterClient[]>('/geoclients/filter-options/clients'),
    ),

  // Scenarios
  listScenarios: (clientId: string) =>
    request<Scenario[]>(`/clients/${clientId}/scenarios`),
  getScenario: (clientId: string, id: string) =>
    request<Scenario>(`/clients/${clientId}/scenarios/${id}`),
  createScenario: (clientId: string, s: Scenario, geoclientId?: string) =>
    mutate(
      () =>
        request<Scenario>(`/clients/${clientId}/scenarios${geoclientQuery(geoclientId)}`, {
          method: 'POST',
          body: JSON.stringify(s),
        }),
      ['clients:content-summary', 'portfolio'],
    ),
  saveScenario: (clientId: string, s: Scenario, geoclientId?: string) =>
    mutate(
      () =>
        request<Scenario>(`/clients/${clientId}/scenarios/${s.id}${geoclientQuery(geoclientId)}`, {
          method: 'PUT',
          body: JSON.stringify(s),
        }),
      ['clients:content-summary', 'portfolio'],
    ),
  deleteScenario: (clientId: string, id: string, geoclientId?: string) =>
    mutate(
      () =>
        request<{ ok: true }>(
          `/clients/${clientId}/scenarios/${id}/delete${geoclientQuery(geoclientId)}`,
          { method: 'POST' },
        ),
      ['clients:content-summary', 'portfolio'],
    ),
  cloneScenario: (clientId: string, id: string, name?: string, geoclientId?: string) =>
    mutate(
      () =>
        request<Scenario>(`/clients/${clientId}/scenarios/${id}/clone${geoclientQuery(geoclientId)}`, {
          method: 'POST',
          body: JSON.stringify({ name }),
        }),
      ['clients:content-summary', 'portfolio'],
    ),

  // Stateless calc
  calculate: (modelId: string, profile: ClientProfile) =>
    request<CalculationResult>('/calculate', {
      method: 'POST',
      body: JSON.stringify({ modelId, profile }),
    }),

  // Staff plans (current-team snapshots, per client)
  listAllStaffPlans: () => request<StaffPlan[]>('/staff-plans'),
  listStaffPlans: (clientId: string) =>
    request<StaffPlan[]>(`/clients/${clientId}/staff-plans`),
  getStaffPlan: (clientId: string, id: string) =>
    request<StaffPlan>(`/clients/${clientId}/staff-plans/${id}`),
  createStaffPlan: (clientId: string, plan: StaffPlan, geoclientId?: string) =>
    mutate(
      () =>
        request<StaffPlan>(`/clients/${clientId}/staff-plans${geoclientQuery(geoclientId)}`, {
          method: 'POST',
          body: JSON.stringify(plan),
        }),
      ['clients:content-summary', 'portfolio'],
    ),
  saveStaffPlan: (clientId: string, plan: StaffPlan, geoclientId?: string) =>
    mutate(
      () =>
        request<StaffPlan>(`/clients/${clientId}/staff-plans/${plan.id}${geoclientQuery(geoclientId)}`, {
          method: 'PUT',
          body: JSON.stringify(plan),
        }),
      ['clients:content-summary', 'portfolio'],
    ),
  deleteStaffPlan: (clientId: string, id: string, geoclientId?: string) =>
    mutate(
      () =>
        request<{ ok: true }>(
          `/clients/${clientId}/staff-plans/${id}/delete${geoclientQuery(geoclientId)}`,
          { method: 'POST' },
        ),
      ['clients:content-summary', 'portfolio'],
    ),

  // Spend sets (independently saved channel spend configurations, per client)
  listSpendSets: (clientId: string) =>
    request<SpendSet[]>(`/clients/${clientId}/spend-sets`),
  getSpendSet: (clientId: string, id: string) =>
    request<SpendSet>(`/clients/${clientId}/spend-sets/${id}`),
  createSpendSet: (clientId: string, set: SpendSet, geoclientId?: string) =>
    mutate(
      () =>
        request<SpendSet>(`/clients/${clientId}/spend-sets${geoclientQuery(geoclientId)}`, {
          method: 'POST',
          body: JSON.stringify(set),
        }),
      ['clients:content-summary'],
    ),
  saveSpendSet: (clientId: string, set: SpendSet, geoclientId?: string) =>
    mutate(
      () =>
        request<SpendSet>(`/clients/${clientId}/spend-sets/${set.id}${geoclientQuery(geoclientId)}`, {
          method: 'PUT',
          body: JSON.stringify(set),
        }),
      ['clients:content-summary'],
    ),
  deleteSpendSet: (clientId: string, id: string, geoclientId?: string) =>
    mutate(
      () =>
        request<{ ok: true }>(
          `/clients/${clientId}/spend-sets/${id}/delete${geoclientQuery(geoclientId)}`,
          { method: 'POST' },
        ),
      ['clients:content-summary'],
    ),

  // Automation Solution Sets (automations + deployment timing, per client; sent in by the automation application)
  listAutomationSolutions: (clientId: string) =>
    request<AutomationSolution[]>(`/clients/${clientId}/automation-solutions`),
  getAutomationSolution: (clientId: string, id: string) =>
    request<AutomationSolution>(`/clients/${clientId}/automation-solutions/${id}`),
  saveAutomationSolution: (clientId: string, solution: AutomationSolution, geoclientId?: string) =>
    mutate(
      () =>
        request<AutomationSolution>(
          `/clients/${clientId}/automation-solutions/${solution.id}${geoclientQuery(geoclientId)}`,
          {
            method: 'PUT',
            body: JSON.stringify(solution),
          },
        ),
      ['clients:content-summary'],
    ),
  deleteAutomationSolution: (clientId: string, id: string, geoclientId?: string) =>
    mutate(
      () =>
        request<{ ok: true }>(
          `/clients/${clientId}/automation-solutions/${id}/delete${geoclientQuery(geoclientId)}`,
          { method: 'POST' },
        ),
      ['clients:content-summary'],
    ),

  // Reporting
  portfolio: () => cached('portfolio', () => request<PortfolioReport>('/reporting/portfolio')),

};

export type AccessRole =
  | 'gia_admin'
  | 'application_admin'
  | 'nbl_calculator_admin'
  | 'regional_admin'
  | 'global_cbl';

export interface AccessPermissions {
  canAccessModelsAndReporting: boolean;
  canSeeAllGeoclients: boolean;
  canCreateScenarios: boolean;
  canCreateStaffPlans: boolean;
  canDeleteContent: boolean;
}

export interface EligibleGeoclient {
  id: string;
  geoclientKey: string;
  code: string;
  name: string;
  status: string;
  marketCode: string;
  marketName: string;
  regionCode: string;
  clientId: string;
  clientUuid: string;
  clientName: string;
  clientStatus: string;
}

export interface GeoclientFilterMarket {
  code: string;
  name: string;
  regionCode: string;
}

export interface GeoclientFilterClient {
  slug: string;
  name: string;
}

export interface AuthUser {
  userId: string;
  email: string;
  displayName: string;
  role: AccessRole | null;
  roles: AccessRole[];
  eligibleGeoclients: EligibleGeoclient[];
  eligibleClientIds: string[];
  permissions: AccessPermissions;
}

export interface AuthProviderStatus {
  configured: boolean;
  enabled: boolean;
  available: boolean;
}

export interface AuthConfig {
  configured: boolean;
  bypass: boolean;
  redirectUri: string;
  providers?: {
    entra: AuthProviderStatus;
    okta: AuthProviderStatus;
  };
}

export interface ClientContentSummary {
  clientId: string;
  scenarioCount: number;
  staffPlanCount: number;
  latestStaffPlan?: {
    id: string;
    clientId: string;
    name: string;
    updatedAt: string;
  };
}

export interface ModelSummary {
  id: string;
  name: string;
  parentId?: string | null;
  version?: number;
  engineMode?: Model['engineMode'];
  createdAt?: string;
  updatedAt?: string;
  notes?: string;
  disciplineCount?: number;
  channelCount?: number;
  /** Base currency of the model's monetary tables. Absent means USD. */
  currency?: Model['currency'];
}

export interface AuthMe {
  authenticated: boolean;
  user?: AuthUser;
  configured?: boolean;
  bypass?: boolean;
  redirectUri?: string;
  providers?: AuthConfig['providers'];
}

/** A scenario the portfolio report could not convert into USD. */
export interface PortfolioCurrencyWarning {
  clientName: string;
  scenarioName: string;
  currency: string;
}

export interface PortfolioReport {
  generatedAt: string;
  /** Currency every dollar figure in this report is normalized to. */
  currency?: string;
  /** Scenarios excluded from the dollar totals for lack of a USD rate. */
  currencyWarnings?: PortfolioCurrencyWarning[];
  totals: {
    clients: number;
    modeledFte: number;
    currentFte: number;
    delta: number;
    y1Released: number;
    y3Released: number;
    y1Dollar: number;
    y3Dollar: number;
  };
  clients: PortfolioClientRow[];
  byIndustry: { key: string; count: number; modeled: number; y3Dollar: number }[];
  byRegion: { key: string; count: number; modeled: number; y3Dollar: number }[];
  byDiscipline: { label: string; current: number; modeled: number; y3: number }[];
}

export interface PortfolioClientRow {
  clientId: string;
  clientName: string;
  industry?: string;
  region?: string;
  modelId: string;
  scenarioId: string;
  scenarioName: string;
  modeledFte: number;
  currentFte: number;
  delta: number;
  y1Fte: number;
  y3Fte: number;
  y3Released: number;
  y1Dollar: number;
  y3Dollar: number;
}
